export type AbsenceLimitWindow = 7 | 14 | 30;

export const ABSENCE_LIMIT_WINDOWS: readonly AbsenceLimitWindow[] = [7, 14, 30] as const;

export const ABSENCE_LIMIT_WINDOW_LABELS: Record<AbsenceLimitWindow, string> = {
  7: 'SETTINGS.ACCESS.ABSENCE_LIMIT.WINDOW.WEEK',
  14: 'SETTINGS.ACCESS.ABSENCE_LIMIT.WINDOW.TWO_WEEKS',
  30: 'SETTINGS.ACCESS.ABSENCE_LIMIT.WINDOW.MONTH',
};

export const ABSENCE_LIMIT_WINDOW_TITLE_LABELS: Record<AbsenceLimitWindow, string> = {
  7: 'SETTINGS.ACCESS.ABSENCE_LIMIT.WINDOW_TITLE.WEEK',
  14: 'SETTINGS.ACCESS.ABSENCE_LIMIT.WINDOW_TITLE.TWO_WEEKS',
  30: 'SETTINGS.ACCESS.ABSENCE_LIMIT.WINDOW_TITLE.MONTH',
};

export interface AbsenceLimitWindowCard {
  Window: AbsenceLimitWindow;
  TitleKey: string;
  LabelKey: string;
  EnabledField: 'Window7Enabled' | 'Window14Enabled' | 'Window30Enabled';
  LimitField: 'Window7Limit' | 'Window14Limit' | 'Window30Limit';
  BlocksField: 'Window7BlocksEntry' | 'Window14BlocksEntry' | 'Window30BlocksEntry';
}

export interface AbsenceLimitWindowRow {
  Card: AbsenceLimitWindowCard;
  IsOn: boolean;
  NoticeLabel: string;
  BlockLabel: string;
}

export interface AbsenceLimitRecapRow {
  LabelKey: string;
  Text: string;
}

export type AbsenceLimitNotification = 'nearLimit' | 'limitReached' | 'entryBlocked';

export const ABSENCE_LIMIT_NOTIFICATIONS: readonly AbsenceLimitNotification[] = [
  'nearLimit',
  'limitReached',
  'entryBlocked',
] as const;

export const ABSENCE_LIMIT_NOTIFICATION_PREFIX = {
  nearLimit: 'NearLimitNotification',
  limitReached: 'LimitReachedNotification',
  entryBlocked: 'EntryBlockedNotification',
} as const satisfies Record<AbsenceLimitNotification, string>;

export const ABSENCE_LIMIT_CONTROL_PREFIX = 'Absence';

export type AbsenceLimitNotificationPrefix =
  (typeof ABSENCE_LIMIT_NOTIFICATION_PREFIX)[AbsenceLimitNotification];

export type AbsenceLimitEnabledField = `${AbsenceLimitNotificationPrefix}Enabled`;
export type AbsenceLimitTitleField = `${AbsenceLimitNotificationPrefix}Title`;
export type AbsenceLimitBodyField = `${AbsenceLimitNotificationPrefix}Body`;

export const ABSENCE_LIMIT_MERGE_FIELDS = [
  '<<PersonName>>',
  '<<TenantName>>',
  '<<EventDate>>',
  '<<EventTime>>',
  '<<AccessPoint>>',
  '<<SchoolClass>>',
  '<<AbsenceCount>>',
  '<<AbsenceLimit>>',
  '<<AbsenceWindow>>',
] as const;

export type AbsenceLimitMergeField = (typeof ABSENCE_LIMIT_MERGE_FIELDS)[number];

export const ABSENCE_LIMIT_TITLE_MAX_LENGTH = 20;
export const ABSENCE_LIMIT_BODY_MAX_LENGTH = 500;
export const ABSENCE_LIMIT_LIMIT_MIN = 1;

export interface AbsenceLimitSettings {
  AbsenceLimitSettingsId: string;
  Window7Enabled: boolean;
  Window7Limit: number | null;
  Window7BlocksEntry: boolean;
  Window14Enabled: boolean;
  Window14Limit: number | null;
  Window14BlocksEntry: boolean;
  Window30Enabled: boolean;
  Window30Limit: number | null;
  Window30BlocksEntry: boolean;
  CountsJustified: boolean;
  AllowManualRelease: boolean;
  RequireManualReleaseReason: boolean;
  NearLimitNotificationEnabled: boolean;
  NearLimitNotificationTitle: string;
  NearLimitNotificationBody: string;
  LimitReachedNotificationEnabled: boolean;
  LimitReachedNotificationTitle: string;
  LimitReachedNotificationBody: string;
  EntryBlockedNotificationEnabled: boolean;
  EntryBlockedNotificationTitle: string;
  EntryBlockedNotificationBody: string;
  AddedOnUtc: string | null;
  AddedBy: string | null;
  UpdatedOnUtc: string | null;
  UpdatedBy: string | null;
}

export type AbsenceLimitSettingsRequest = Partial<
  Omit<
    AbsenceLimitSettings,
    'AbsenceLimitSettingsId' | 'AddedOnUtc' | 'AddedBy' | 'UpdatedOnUtc' | 'UpdatedBy'
  >
>;

export interface AbsenceLimitSettingsState {
  absenceLimitSettings: AbsenceLimitSettings | null;
}

export const initialAbsenceLimitSettingsState: AbsenceLimitSettingsState = {
  absenceLimitSettings: null,
};
